import { useState } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Clock3, Loader2, Mail, RotateCcw } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { parseApiErrorObject } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProjectPicker, type PickerProject } from "@/components/calls/ProjectPicker";

type IgnoredEmail = {
  id: number;
  emailAccountId: string;
  emailUID: number;
  subject: string;
  fromAddress: string;
  fromName: string | null;
  emailDate: string | null;
  ignoredAt: string;
  ignoredBy: { id: number; name: string } | null;
};

type IgnoredEmailPage = {
  items: IgnoredEmail[];
  nextCursor: string | null;
  hasMore: boolean;
};

function displayDate(value: string | null): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : format(date, "MMM d, yyyy HH:mm");
}

/**
 * Admin-only recovery UI for the durable ignored-email markers.  It does not
 * optimistically remove a row: the server removes the marker only after the
 * assignment or approval fanout commits, so cancellation and failures leave
 * this history visible and recoverable.
 */
export function IgnoredEmailRecovery() {
  const { toast } = useToast();
  const [selectedEmail, setSelectedEmail] = useState<IgnoredEmail | null>(null);
  const [selectedProject, setSelectedProject] = useState<PickerProject | null>(null);

  const ignoredQuery = useInfiniteQuery<IgnoredEmailPage>({
    queryKey: ["/api/email-notifications/ignored"],
    queryFn: async ({ pageParam }) => {
      const cursor = typeof pageParam === "string" ? `&cursor=${encodeURIComponent(pageParam)}` : "";
      const response = await apiRequest(
        "GET",
        `/api/email-notifications/ignored?limit=50${cursor}`,
      );
      return response.json() as Promise<IgnoredEmailPage>;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const ignoredEmails = ignoredQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const recoverMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEmail || !selectedProject) throw new Error("Select a project first");
      const response = await apiRequest(
        "POST",
        `/api/email-notifications/ignored/${selectedEmail.id}/reassign`,
        { projectId: selectedProject.id },
      );
      return response.json() as Promise<{ status: "assigned" | "pending_approval" }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-notifications/ignored"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email-notifications/count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      setSelectedEmail(null);
      setSelectedProject(null);
      toast({
        title: result.status === "pending_approval" ? "Sent for approval" : "Email recovered",
        description: result.status === "pending_approval"
          ? "The project team must approve this recovered email."
          : "The email was assigned and a pulse was created.",
      });
    },
    onError: (error: unknown) => {
      const { body } = parseApiErrorObject(error);
      const message =
        (body && typeof body === "object" && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        "Failed to recover this email.";
      toast({
        title: "Recovery failed",
        description: String(message),
        variant: "destructive",
      });
    },
  });

  return (
    <section className="space-y-3 border-t border-gray-600 pt-6" data-testid="ignored-email-recovery">
      <div className="flex items-center gap-2">
        <RotateCcw className="h-4 w-4 text-amber-400" />
        <h4 className="text-md font-medium text-white">Recently ignored emails</h4>
        <Badge variant="outline" className="border-gray-600 text-gray-400">Admin only</Badge>
      </div>
      <p className="text-xs text-gray-400">
        Ignored emails remain retained for recovery. They are ordered by the ignore action;
        the original email date is shown separately.
      </p>

      {ignoredQuery.isLoading && (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading ignored emails…
        </div>
      )}
      {ignoredQuery.isError && (
        <div className="rounded border border-red-800 bg-red-950/20 p-3 text-sm text-red-300" role="alert">
          Ignored emails could not be loaded.
        </div>
      )}
      {!ignoredQuery.isLoading && !ignoredQuery.isError && ignoredEmails.length === 0 && (
        <div className="rounded border border-gray-700 bg-gray-800/40 p-5 text-center text-sm text-gray-400">
          <Mail className="mx-auto mb-2 h-6 w-6 opacity-60" />
          No recently ignored emails.
        </div>
      )}
      {ignoredEmails.length > 0 && (
        <div className="overflow-hidden rounded border border-gray-700">
          <div className="divide-y divide-gray-700">
            {ignoredEmails.map((email) => (
              <div key={email.id} className="flex items-start justify-between gap-3 bg-gray-800/40 p-3">
                <div className="min-w-0 space-y-1 text-sm">
                  <div className="truncate font-medium text-white" title={email.subject || "(No subject)"}>
                    {email.subject || "(No subject)"}
                  </div>
                  <div className="truncate text-gray-300">
                    {email.fromName ? `${email.fromName} · ` : ""}{email.fromAddress || "Unknown sender"}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                    <span title="Original email date">Email: {displayDate(email.emailDate)}</span>
                    <span className="inline-flex items-center gap-1" title="Ignored at">
                      <Clock3 className="h-3 w-3" /> Ignored: {displayDate(email.ignoredAt)}
                    </span>
                    <span>Ignored by: {email.ignoredBy?.name || "Former user"}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => {
                    setSelectedEmail(email);
                    setSelectedProject(null);
                  }}
                  data-testid={`button-recover-ignored-${email.id}`}
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                  Reassign
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {ignoredQuery.hasNextPage && (
        <Button
          type="button"
          variant="outline"
          onClick={() => ignoredQuery.fetchNextPage()}
          disabled={ignoredQuery.isFetchingNextPage}
          data-testid="button-load-more-ignored-emails"
        >
          {ignoredQuery.isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Load more
        </Button>
      )}

      <Dialog
        open={!!selectedEmail}
        onOpenChange={(open) => {
          if (!open && !recoverMutation.isPending) {
            setSelectedEmail(null);
            setSelectedProject(null);
          }
        }}
      >
        <DialogContent className="border-gray-600 bg-gray-900 text-white">
          <DialogHeader>
            <DialogTitle>Recover ignored email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-300">
              Choose a project. Current project approval rules still apply; the email is
              restored only after the assignment or approval request commits.
            </p>
            <ProjectPicker
              value={selectedProject?.id || ""}
              selected={selectedProject}
              onChange={(projectId) => {
                if (!projectId) {
                  setSelectedProject(null);
                  return;
                }
                setSelectedProject({ id: projectId, projectName: projectId });
              }}
              disabled={recoverMutation.isPending}
              testId="ignored-recovery-project-picker"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedEmail(null);
                setSelectedProject(null);
              }}
              disabled={recoverMutation.isPending}
              data-testid="button-cancel-ignored-recovery"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => recoverMutation.mutate()}
              disabled={!selectedProject || recoverMutation.isPending}
              data-testid="button-submit-ignored-recovery"
            >
              {recoverMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Recover email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}